import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../lib/api';
import {
    ProviderConfig,
    ProviderConfigsResponse,
    UpdateProviderConfigRequest,
    AlertConfig,
    UpdateAlertConfigRequest,
    ThresholdConfig,
    UpdateThresholdConfigRequest,
    IntegrationGuideResponse,
} from '../types';
import toast from 'react-hot-toast';

// Provider Settings
export const useProviderConfigs = () => {
    return useQuery({
        queryKey: ['settings', 'providers'],
        queryFn: async () => {
            const { data } = await api.get<{ success: boolean; data: ProviderConfigsResponse }>('/settings/providers');
            return data.data;
        },
    });
};

// Integration Guide
export const useIntegrationGuide = () => {
    return useQuery({
        queryKey: ['settings', 'integration-guide'],
        queryFn: async () => {
            const { data } = await api.get<{ success: boolean; data: IntegrationGuideResponse }>('/settings/providers/integration-guide');
            return data.data;
        },
    });
};

export const useUpdateProviderConfig = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (configData: UpdateProviderConfigRequest & { id?: number }) => {
            // If updating existing provider, use PUT with provider name in URL
            // If creating new provider, use POST with provider in body
            if (configData.id) {
                // Update existing - PUT /providers/{provider}
                const { data } = await api.put<{ success: boolean; data: ProviderConfig; message: string }>(
                    `/settings/providers/${configData.provider}`,
                    {
                        environment: configData.environment,
                        webhook_secret: configData.webhook_secret,
                        is_active: configData.is_active,
                        settings: configData.settings,
                    }
                );
                return data.data;
            } else {
                // Create new - POST /providers
                const { data } = await api.post<{ success: boolean; data: ProviderConfig; message: string }>(
                    '/settings/providers',
                    {
                        provider: configData.provider,
                        environment: configData.environment,
                        webhook_secret: configData.webhook_secret,
                        is_active: configData.is_active,
                        settings: configData.settings,
                    }
                );
                return data.data;
            }
        },
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({ queryKey: ['settings', 'providers'] });
            toast.success(variables.id ? 'Provider updated successfully' : 'Provider added successfully');
        },
        onError: (error: any) => {
            const errorCode = error.response?.data?.error?.code;
            const errorData = error.response?.data?.error;
            
            // Handle provider limit exceeded with detailed message and link
            if (errorCode === 'PROVIDER_LIMIT_EXCEEDED') {
                const currentPlan = errorData.current_plan_name || 'current';
                const upgradePlan = errorData.upgrade_to || 'Basic';
                const upgradeLimit = errorData.upgrade_limit || '3';
                
                toast.error(
                    `Provider limit reached! Your ${currentPlan} plan allows ${errorData.limit} providers. Upgrade to ${upgradePlan} for ${upgradeLimit} providers.`,
                    { duration: 5000 }
                );
                return;
            }
            
            const errorMessage =
                error.response?.data?.error?.message ||
                error.response?.data?.message ||
                'Failed to update provider configuration';
            toast.error(errorMessage);
        },
    });
};

// Alert Settings
export const useAlertConfigs = () => {
    return useQuery({
        queryKey: ['settings', 'alerts'],
        queryFn: async () => {
            const { data } = await api.get<{ success: boolean; data: AlertConfig[] }>('/settings/alerts');
            return data.data;
        },
    });
};

export const useUpdateAlertConfig = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (configData: UpdateAlertConfigRequest) => {
            const { data } = await api.post<{ success: boolean; data: AlertConfig }>(
                '/settings/alerts',
                configData
            );
            return data.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['settings', 'alerts'] });
            toast.success('Alert configuration updated');
        },
        onError: () => {
            toast.error('Failed to update alert configuration');
        },
    });
};

// Threshold Settings
export const useThresholdConfigs = () => {
    return useQuery({
        queryKey: ['settings', 'thresholds'],
        queryFn: async () => {
            const { data } = await api.get<{ success: boolean; data: ThresholdConfig[] }>('/settings/thresholds');
            return data.data;
        },
    });
};

export const useUpdateThresholdConfig = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (configData: UpdateThresholdConfigRequest) => {
            const { data } = await api.post<{ success: boolean; data: ThresholdConfig }>(
                '/settings/thresholds',
                configData
            );
            return data.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['settings', 'thresholds'] });
            toast.success('Threshold configuration updated');
        },
        onError: () => {
            toast.error('Failed to update threshold configuration');
        },
    });
};
